import { v4 as uuid } from "uuid";
import { GroupedConnectionPoint } from "../model/viewModel/groupedConnectionPoint";
import { LatLng } from "../model/viewModel/simpleTypes";
import { ConnectionPoint } from "../model/viewModel/connectionPoint";
import { GroupedConnection } from "../model/viewModel/groupedConnection";

export function copyListedProps<T extends object>(
  from: Partial<T>,
  keys: readonly (keyof T)[],
): Partial<T> {
  const out: Partial<T> = {};
  keys.forEach((k) => {
    if (Object.prototype.hasOwnProperty.call(from, k)) {
      out[k] = from[k];
    }
  });
  return out;
}

// If updated, please reflect in the Wiki
export const ALLOW_WELDER = [
  "rating",
  "ratingIsDefault",
  "ratingUnit",
  "numberOfPhases",
  "numberOfPhasesIsDefault",
  "numberOfBursts",
  "numberOfBurstsIsDefault",
  "numberOfBurstsUnit",
  "pointOfWaveControl",
  "powerFactor",
  "powerFactorIsDefault",
  "efficiency",
  "efficiencyIsDefault",
  "hvFaultLevel",
  "xrRatio",
  "voc",
  "vocIsDefault",
  "styles",
  "phaseAllocation",
] as const;

// If updated, please reflect in the Wiki
export const ALLOW_MOTOR = [
  "rating",
  "ratingIsDefault",
  "ratingUnit",
  "numberOfPhases",
  "numberOfPhasesIsDefault",
  "starter",
  "starterIsDefault",
  "startingPowerFactor",
  "startingPowerFactorIsDefault",
  "startingCurrentOverride",
  "numberOfStarts",
  "numberOfStartsIsDefault",
  "numberOfStartsUnit",
  "efficiency",
  "efficiencyIsDefault",
  "hvFaultLevel",
  "xrRatio",
  "styles",
] as const;

// If updated, please reflect in the Wiki
export const ALLOW_POINT_OF_CONNECTION = [
  "voltDropPercent",
  "loopResistanceMilliohm",
  "includesTransformerResistance",
  "styles",
] as const;

// If updated, please reflect in the Wiki
export const ALLOW_TRANSFORMER = [
  "nameplateRating",
  "nameplateRatingIsDefault",
  "cyclicRating",
  "mounting",
  "systemVoltage",
  "numberOfPhases",
  "numberOfPhasesIsDefault",
  "fuseSelection",
  "fuseSelectionIsDefault",
  "fuseRating",
  "autoSelect",
  //  "groupedConnectionPoints", handled within form temporarily
  "ratingType",
  "hvFaultLevel",
  "hvFaultLevelIsDefault",
  "xrRatio",
  "xrRatioIsDefault",
  "designVoltage",
  "primaryVoltagekV",
  "primaryVoltagekVIsDefault",
] as const;

// If updated, please reflect in the Wiki
export const ALLOW_CONNECTION_POINT = [
  "consumerType",
  "consumerTypeIsDefault",
  "autoPhase",
  "phaseAutoConsumers",
  "phase1Consumers",
  "phase2Consumers",
  "phase3Consumers",
  "balancedLoad",
  "unbalancePercent",
  "lowLoadedPhase",
  "consumptionValue1",
  "consumptionValue1IsDefault",
  "consumptionValue2",
  "consumptionValue2IsDefault",
  "consumptionType",
  "consumptionTypeIsDefault",
  "numberOfPhases",
  "numberOfPhasesIsDefault",
  "styles",
  "isSubGroupConnection",
  "isGenerator",
  "phaseMap",
  "status",
  "cutOutSize",
  "multiOccupancyContainer",
  "multiOccupancy",
  "moSpareWays",
  "linkBox",
  "potEnds",
  "pole",
  "count",
  "classIsDefault",
] as const;

// If updated, please reflect in the Wiki
export const ALLOW_CABLE = [
  "cableType",
  "cableTypeIsDefault",
  "autoSelect",
  "cableGroup",
  "cableGroupIsDefault",
  "numberOfPhases",
  "numberOfPhasesIsDefault",
  "styles",
  "groupedConnectionPoints",
  "feederFuseSize",
  "hasFuseSize",
  "ducting",
  "ratingType",
  "status",
] as const;

// If updated, please reflect in the Wiki
export const ALLOW_NODE = [
  "styles",
  "status",
  "linkBox",
  "potEnds",
  "pole",
  "poleHeightIsDefault",
  "stayAngleIsDefault",
  "poleTermLengthIsDefault",
  "fuse",
  "fuseIsDefault",
] as const;

export const updateGroupedConnectionPointsForCopyPasteProperties = (
  newGroupedConnectionPoints: GroupedConnectionPoint[] | null,
  targetGeometry: LatLng,
) => {
  return (newGroupedConnectionPoints ?? []).map((gcp) => {
    const newId = uuid();

    const subGroupConnectionPoints = Array.isArray(gcp.subGroupConnectionPoints)
      ? gcp.subGroupConnectionPoints.map((sub) => {
          const pickedSubConnections = copyListedProps(sub, ALLOW_CONNECTION_POINT);
          return {
            ...pickedSubConnections,
            id: uuid(),
            parentId: newId,
            geometry: targetGeometry,
          };
        })
      : [];

    const pickedConnectionPoint = copyListedProps(gcp, ALLOW_CONNECTION_POINT);

    return {
      ...pickedConnectionPoint,
      id: newId,
      geometry: targetGeometry,
      subGroupConnectionPoints,
    };
  });
};

export const updateGroupedConnectionForCopyAndPaste = (
  propertiesToPaste: ConnectionPoint,
  existingGroupedConnectionProperties: GroupedConnection,
  isNode: boolean,
  canBePole: boolean,
) => {
  const root = existingGroupedConnectionProperties;

  const groupedConnectionPoints = updateGroupedConnectionPointsForCopyPasteProperties(
    propertiesToPaste.groupedConnectionPoints,
    root.geometry,
  ) as GroupedConnectionPoint[];

  const pickedNodeProperties = isNode ? copyListedProps(propertiesToPaste, ALLOW_NODE) : {};

  const linkBox = copyLinkBox(propertiesToPaste.linkBox);
  const potEnds = copyPotEnds(propertiesToPaste.potEnds);
  const fuse = copyFuse(propertiesToPaste.fuse);

  const updated = new GroupedConnection(
    root.id,
    root.geometry,
    groupedConnectionPoints,
    root.styles,
    root.overrideGroundType,
  );

  const pole = (() => {
    if (!canBePole) return undefined;

    const existingIsPole = root.pole !== undefined && root.pole !== null;
    const propertiesToPasteIsPole =
      propertiesToPaste.pole !== undefined && propertiesToPaste.pole !== null;

    if (existingIsPole && !propertiesToPasteIsPole) {
      // Use existing pole
      return root.pole;
    } else if (existingIsPole && propertiesToPasteIsPole) {
      return propertiesToPaste.pole;
    }
    return undefined;
  })();

  const returnObject = {
    ...updated,
    ...pickedNodeProperties,
    linkBox: linkBox,
    pole: pole,
    potEnds: potEnds,
    fuse: fuse,
    multiOccupancy: propertiesToPaste.multiOccupancy,
  };
  return returnObject;
};

export const copyLinkBox = (linkBox: any): any => {
  const copiedLinkBox =
    linkBox !== undefined && linkBox !== null
      ? { ...linkBox, id: uuid(), twoWayConnectivityIsClosed: false }
      : undefined;
  return copiedLinkBox;
};

export const copyPotEnds = (potEnds: any): any => {
  const copiedPotEnds = Array.isArray(potEnds)
    ? potEnds.map((potEnd) => ({
        ...potEnd,
        id: uuid(),
      }))
    : undefined;
  return copiedPotEnds;
};

export const copyFuse = (fuse: any): any => {
  const copiedFuse = fuse !== undefined && fuse !== null ? fuse : undefined;
  return copiedFuse;
};
